import React, { useCallback, useRef, useState } from 'react';

/**
 * Drag-and-drop / browse area for the CSV upload, including the inline
 * file-level error state (wrong encoding, too large, ragged columns, ...).
 */
const CsvDropzone = ({ onFileSelected, error, disabled = false }) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const openFileDialog = useCallback(() => {
    if (disabled) return;
    // Clear here rather than in the change handler: the selection has to stay
    // attached to the input while the upload reads it, and clearing before the
    // dialog opens is what makes re-picking the same file fire `change` again.
    if (inputRef.current) inputRef.current.value = '';
    inputRef.current.click();
  }, [disabled]);

  const handleInputChange = useCallback(
    event => {
      const file = event.target.files?.[0];
      if (file) onFileSelected(file);
    },
    [onFileSelected]
  );

  const handleDragOver = useCallback(
    event => {
      event.preventDefault();
      if (!disabled) setIsDragging(true);
    },
    [disabled]
  );

  const handleDragLeave = useCallback(event => {
    event.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    event => {
      event.preventDefault();
      setIsDragging(false);
      if (disabled) return;

      const file = event.dataTransfer?.files?.[0];
      if (file) onFileSelected(file);
    },
    [disabled, onFileSelected]
  );

  const borderClass = error
    ? 'border-red-400 bg-red-50'
    : isDragging
    ? 'border-blue-500 bg-blue-50'
    : 'border-gray-300 bg-white';

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`border-2 border-dashed rounded-lg px-6 py-12 text-center transition-colors duration-150 ${borderClass}`}
    >
      <input
        ref={inputRef}
        type='file'
        accept='.csv,text/csv'
        onChange={handleInputChange}
        className='hidden'
        data-testid='csv-file-input'
      />

      <svg
        className={`w-10 h-10 mx-auto mb-4 ${
          error ? 'text-red-500' : 'text-blue-600'
        }`}
        fill='none'
        stroke='currentColor'
        viewBox='0 0 24 24'
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
          d='M9 13h6m-3-3v6m-7 5h14a2 2 0 002-2V7.414a1 1 0 00-.293-.707l-3.414-3.414A1 1 0 0015.586 3H5a2 2 0 00-2 2v14a2 2 0 002 2z'
        />
      </svg>

      <p className='text-base text-gray-900'>Drag and drop CSV file here</p>
      <p className='text-sm text-gray-500 my-2'>or</p>

      <button
        type='button'
        onClick={openFileDialog}
        disabled={disabled}
        className='bg-blue-600 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
      >
        Browse Files
      </button>

      <p className='mt-4 text-sm text-gray-400'>
        CSV • UTF-8 • Max 10 MB • Max 10,000 rows
      </p>

      {error && (
        <p className='mt-3 text-sm text-red-600 whitespace-pre-line'>{error}</p>
      )}
    </div>
  );
};

export default CsvDropzone;
